import React, { useState } from 'react';
import { ArrowLeft, UserPlus, User, Mail, Building, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    password: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      alert('Please fill in all required fields');
      return;
    }
    
    if (formData.password.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }
    
    if (!acceptedTerms) {
      alert('Please accept the Terms & Conditions to continue');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE}/api/community/register`, formData);
      
      if (response.data.message === 'User registered successfully') {
        // Store user info in localStorage for persistence
        const userData = {
          id: response.data.user_id,
          name: response.data.name,
          email: response.data.email,
          company: formData.company,
          role: 'member'
        };
        
        localStorage.setItem('currentUser', JSON.stringify(userData));
        
        alert('Registration successful! Welcome to ShansoConsult Community!');
        navigate('/community');
      }
    } catch (error) {
      console.error('Registration failed:', error);
      alert(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="dark-container min-h-screen" style={{ paddingTop: '100px' }}>
      <div className="max-w-md mx-auto px-6 py-8">
        <Link to="/community">
          <Button className="btn-secondary mb-8">
            <ArrowLeft size={20} />
            Back to Community
          </Button>
        </Link>

        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus size={40} className="text-turquoise-400" />
          </div>
          <h1 className="display-large mb-4">
            Join ShansoConsult <span className="turquoise-text-gradient">Community</span>
          </h1>
          <p className="body-large text-gray-300">
            Connect with business excellence professionals and unlock your potential.
          </p>
        </div>

        <form onSubmit={handleSignup} className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6 mb-6">
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <User size={16} className="inline mr-2" />
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-turquoise-400 focus:outline-none transition-colors"
                placeholder="Enter your full name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Mail size={16} className="inline mr-2" />
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-turquoise-400 focus:outline-none transition-colors"
                placeholder="Enter your email"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Building size={16} className="inline mr-2" />
                Company/Role
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-turquoise-400 focus:outline-none transition-colors"
                placeholder="Your company or role (optional)"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Lock size={16} className="inline mr-2" />
                Create Password *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength="6"
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-turquoise-400 focus:outline-none transition-colors"
                placeholder="Create a secure password (min. 6 chars)"
              />
            </div>
          </div>
          
          {/* Terms & Conditions */}
          <div className="mb-6">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 h-4 w-4 text-turquoise-400 bg-gray-800 border-gray-600 rounded focus:ring-turquoise-400 focus:ring-2"
              />
              <span className="text-sm text-gray-300">
                I agree to the{' '}
                <Link to="/community/guidelines" className="text-turquoise-400 hover:text-turquoise-300 underline">
                  Community Posting Guidelines
                </Link>{' '}
                and{' '}
                <Link to="/privacy-policy" className="text-turquoise-400 hover:text-turquoise-300 underline">
                  Privacy Policy
                </Link>
              </span>
            </label>
          </div>
          
          <Button 
            type="submit" 
            className="btn-primary-enhanced w-full"
            disabled={isLoading || !acceptedTerms}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Creating Account...
              </>
            ) : (
              <>
                <UserPlus size={20} />
                Create Account
              </>
            )}
          </Button>
        </form>

        <div className="text-center space-y-4">
          <div>
            <p className="text-gray-400 mb-4">Already have an account?</p>
            <Link to="/login">
              <Button className="btn-secondary w-full">
                Sign In Instead
              </Button>
            </Link>
          </div>
          
          <div className="text-center">
            <Link to="/forgot-password" className="text-turquoise-400 hover:text-turquoise-300 text-sm transition-colors">
              Forgot your password?
            </Link>
          </div>
        </div>

        <div className="mt-8 p-4 bg-gradient-to-r from-turquoise-500/10 to-blue-500/10 border border-turquoise-500/20 rounded-lg">
          <h3 className="text-turquoise-400 font-medium mb-2">What You'll Get:</h3>
          <ul className="text-gray-400 text-sm space-y-1">
            <li>• Connect with industry professionals</li>
            <li>• Access exclusive training materials</li>
            <li>• Share insights and best practices</li>
            <li>• Join discussions on business excellence</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Signup;